/**
 * Sentry Error Tracking and Performance Monitoring Configuration
 *
 * This module initializes and configures Sentry for the SSN frontend application.
 * Sentry is only enabled in production to avoid noise during development.
 *
 * Features:
 * - Error tracking with full stack traces
 * - Performance monitoring (page loads, route changes)
 * - User context enrichment (anonymous session tracking)
 * - Automatic breadcrumb collection
 *
 * Usage:
 * ```typescript
 * import { initSentry } from '@/lib/sentry';
 *
 * // Call before React renders in main.tsx
 * initSentry();
 * ```
 */

import * as Sentry from '@sentry/react';

/**
 * Sentry DSN for the SSN Web project
 * This is safe to be public - DSN only allows sending events, not reading them
 */
const SENTRY_DSN =
  'https://617e7352772da70569b2b3d3f904a2b9@o4510585120096256.ingest.de.sentry.io/4510624747028560';

/**
 * Common benign errors to filter out from Sentry
 * These are browser/library errors that don't indicate real problems
 */
const IGNORED_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  /Loading chunk .* failed/,
  /Failed to fetch dynamically imported module/,
  'Network request failed',
  'AbortError',
];

/**
 * Get viewport size category for tagging
 */
function getViewportCategory(): string {
  if (typeof window === 'undefined') return 'unknown';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Get or create anonymous session ID for user tracking
 * Uses the existing ssn_session_id from sessionStorage if available
 */
function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return 'unknown';

  const key = 'ssn_session_id';
  let sessionId = sessionStorage.getItem(key);

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }

  return sessionId;
}

/**
 * Extract sector from current URL path
 * Returns undefined if not on a sector page
 */
function getCurrentSector(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const path = window.location.pathname;

  // Match sector pages
  if (path.startsWith('/sector/')) {
    const sector = path.split('/')[2];
    return sector; // ai, climate, deeptech, life-science
  }

  // Match focus area pages
  if (path.startsWith('/focus-areas/')) {
    const area = path.split('/')[2];
    return area; // ai-autonomous, energy
  }

  return undefined;
}

/**
 * Set user context for Sentry
 * Uses anonymous session ID - no PII collected
 */
export function setUserContext(): void {
  if (!import.meta.env.PROD) return;

  const sessionId = getSessionId();

  Sentry.setUser({
    id: sessionId,
  });

  // Set custom tags
  Sentry.setTag('viewport', getViewportCategory());

  const sector = getCurrentSector();
  if (sector) {
    Sentry.setTag('sector', sector);
  }
}

/**
 * Update Sentry context on route change
 * Call this from a route change listener
 */
export function updateRouteContext(routeName: string): void {
  if (!import.meta.env.PROD) return;

  Sentry.setTag('route', routeName);
  Sentry.setTag('viewport', getViewportCategory());

  const sector = getCurrentSector();
  if (sector) {
    Sentry.setTag('sector', sector);
  } else {
    // Remove sector tag if not on a sector page
    Sentry.setTag('sector', undefined);
  }

  // Add breadcrumb for navigation
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigated to ${routeName}`,
    level: 'info',
    data: {
      url: window.location.href,
      referrer: document.referrer || 'direct',
    },
  });
}

/**
 * Initialize Sentry error tracking and performance monitoring
 *
 * Must be called before React renders to capture all errors.
 * Only initializes in production environment.
 */
export function initSentry(): void {
  // Only initialize in production
  if (!import.meta.env.PROD) {
    console.log('[Sentry] Disabled in development mode');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment configuration
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,

    // Release tracking - uses version from vite.config.ts
    release: `ssn-web@${__APP_VERSION__}`,

    // Enable default PII collection (IP address)
    sendDefaultPii: true,

    // Performance monitoring
    integrations: [Sentry.browserTracingIntegration()],

    // Control which URLs get distributed tracing headers
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

    // Sample 100% of transactions initially
    // Adjust based on traffic volume after launch
    tracesSampleRate: 1.0,

    // Filter out common benign errors
    ignoreErrors: IGNORED_ERRORS,

    // Before sending, enrich with additional context
    beforeSend(event) {
      // Add viewport info to every event
      if (event.tags) {
        event.tags.viewport = getViewportCategory();
      }
      return event;
    },

    // Before breadcrumb, filter noisy console messages
    beforeBreadcrumb(breadcrumb) {
      // Skip console breadcrumbs from development logging
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      return breadcrumb;
    },
  });

  // Set initial user context
  setUserContext();
}

/**
 * Manually capture an exception with additional context
 * Use this for caught errors that should still be reported
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (!import.meta.env.PROD) {
    console.error('[Sentry] Would capture exception:', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Add a breadcrumb for tracing user actions
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  if (!import.meta.env.PROD) return;

  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data,
  });
}

// Re-export Sentry for direct access when needed
export { Sentry };
